// react
import { useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { sp } from '@/theme/mui';

// components
import { Card } from '@/components/atoms/Card/Card';
import { EmailPreview } from '@/components/atoms/EmailPreview/EmailPreview';
import { TextArea } from '@/components/atoms/TextArea/TextArea';

/**
 * InviteEmailPreview
 */
export const InviteEmailPreview = ({
  emailTemplate,
  personalizedMessage,
  defaultPersonalizedMessage,
  setPersonalizedMessage,
  clientName,
  inviterName,
}: InviteEmailPreviewProps) => {
  // memos
  const msg = useMemo(
    () =>
      personalizedMessage === undefined
        ? defaultPersonalizedMessage
        : personalizedMessage,
    [personalizedMessage, defaultPersonalizedMessage],
  );

  const generated = useMemo(() => {
    return (emailTemplate || '')
      .replace('{{.PersonalizedMessage}}', msg || '')
      .replace(
        '<img src="cid:qr_code0.png" alt="register QR code" width="150px" height="150px" />',
        '<div style="width: 150px; height: 150px; background: #eee; border-radius: 8px;"></div>',
      );
  }, [msg]);

  const subject = useMemo(
    () =>
      `${clientName} here's your invite from ${inviterName} to use KareHero: Care Concierge in your pocket`,
    [clientName, inviterName],
  );

  useEffect(() => {
    if (personalizedMessage === undefined) {
      setPersonalizedMessage(defaultPersonalizedMessage || '');
    }
  }, [defaultPersonalizedMessage, setPersonalizedMessage, personalizedMessage]);

  return (
    <Wrapper>
      <Section>
        <SubTitle>Add Personalized Note</SubTitle>
        <Card color='blue' isBordered={false} isPaddingSm>
          <TextArea size='md' value={msg} onChange={setPersonalizedMessage} />
        </Card>
      </Section>
      <Section>
        <SubTitle>Email Preview</SubTitle>
        <EmailPreview subject={subject} html={generated} />
      </Section>
    </Wrapper>
  );
};

// styles
const Wrapper = ({ children }: { children?: React.ReactNode }) => (
  <Stack
    sx={{
      gap: sp[24],
      width: '100%',
      height: '100%',
      p: sp[20],
      overflowY: 'auto',
    }}
  >
    {children}
  </Stack>
);
const Section = ({ children }: { children?: React.ReactNode }) => (
  <Stack sx={{ gap: sp[8] }}>{children}</Stack>
);
const SubTitle = ({ children }: { children?: React.ReactNode }) => (
  <Box
    sx={{
      typography: 'headingXs',
      color: '#003413',
      textAlign: 'left',
      pb: sp[8],
    }}
  >
    {children}
  </Box>
);

interface InviteEmailPreviewProps {
  emailTemplate: string;
  personalizedMessage?: string;
  defaultPersonalizedMessage?: string;
  setPersonalizedMessage: (msg: string) => void;
  clientName: string;
  inviterName: string;
}

export default InviteEmailPreview;
